import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { StudyDemoIdentityStrip } from '@/pages/try/studyExperience/StudyDemoIdentityStrip';
import {
  useEffect,
  useContext,
  useId,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  Pencil,
  Redo2,
  Trash2,
  Download,
  BookOpen,
  ChevronDown,
  Printer,
  Plus,
} from 'lucide-react';
import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { LearnViewportContext } from '@/app/LearnViewportContext';
import { WorkingPageManagement } from './WorkingPageManagement';
import { WorkingCanvasHeader } from './WorkingCanvasHeader';
import { WorkingToolbar } from './WorkingToolbar';
import { WorkingTextSteps } from './WorkingTextSteps';
import { WorkingSheet } from './WorkingSheet';
import {
  WorkingOverlayContext,
  WORKING_PANEL,
  useWorkingViewport,
  type WorkingPanel,
} from './workingOverlay';
import { AcademyRichText } from '../academy/AcademyRichText';
import {
  CANVAS_WIDTH,
  MAX_CANVAS_BYTES,
  MAX_CANVAS_HEIGHT,
  containsPoint,
  inkPath,
  moveStrokes,
  strokeNear,
  type WorkingContent,
  type InkStroke,
  type InkPoint,
} from './workingCanvas';

interface Props {
  value: WorkingContent;
  onChange: (value: WorkingContent) => void;
  locked?: boolean;
  disabled?: boolean;
  status?: string;
  question?: string | null;
  contextLabel?: string;
  management?: ReactNode;
  tutor?: ReactNode;
  startOpen?: boolean;
  startWithTutor?: boolean;
  /** The material workspace already owns navigation and the viewport. */
  embedded?: boolean;
  onQuestionChange?: (question: string) => void;
}
type Tool = 'pen' | 'eraser' | 'lasso' | 'move' | 'pan';
const colours = { ink: 'text-ink', sky: 'text-brand-sky', coral: 'text-brand-coral' };

export function MathWorkingCanvas({
  value,
  onChange,
  locked = false,
  disabled = false,
  status,
  question,
  contextLabel = 'Mathematics',
  management,
  tutor,
  startOpen = false,
  startWithTutor = false,
  embedded = false,
  onQuestionChange,
}: Props) {
  const containingViewport = useContext(LearnViewportContext);
  const blocked = locked || disabled;
  const [fullScreen, setFullScreen] = useState(startOpen);
  const expanded = fullScreen || embedded;
  const [panel, setPanel] = useState<WorkingPanel>(startWithTutor ? WORKING_PANEL.TUTOR : null);
  const viewport = useWorkingViewport(fullScreen);
  const showManagement = panel === WORKING_PANEL.MANAGEMENT;
  const showText = panel === WORKING_PANEL.TEXT;
  const showMore = panel === WORKING_PANEL.PREFERENCES;
  const setShowManagement = (open: boolean) => setPanel(open ? WORKING_PANEL.MANAGEMENT : null);
  const setShowText = (open: boolean) => setPanel(open ? WORKING_PANEL.TEXT : null);
  const setShowMore = (open: boolean) => setPanel(open ? WORKING_PANEL.PREFERENCES : null);
  const [showQuestion, setShowQuestion] = useState(false);
  const [penWidth, setPenWidth] = useState(4);
  const [fitHeight, setFitHeight] = useState(0);
  const workspace = useRef<HTMLElement>(null);
  const hadFullScreen = useRef(false);
  const paperViewport = useRef<HTMLDivElement>(null);
  const displayedHeight = Math.min(
    MAX_CANVAS_HEIGHT,
    Math.max(value.height, expanded ? fitHeight : 0),
  );
  const displayedHeightRef = useRef(displayedHeight);
  displayedHeightRef.current = displayedHeight;
  useEffect(() => {
    if (!fullScreen) {
      if (hadFullScreen.current)
        workspace.current?.querySelector<HTMLButtonElement>('[data-open-working]')?.focus();
      hadFullScreen.current = false;
      return;
    }
    hadFullScreen.current = true;
    const opener = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const background = Array.from((containingViewport ?? document.body).children)
      .filter((node) => node !== workspace.current && !node.contains(workspace.current))
      .map((node) => ({
        node,
        inert: node.hasAttribute('inert'),
        hidden: node.getAttribute('aria-hidden'),
      }));
    background.forEach(({ node }) => {
      node.setAttribute('inert', '');
      node.setAttribute('aria-hidden', 'true');
    });
    workspace.current?.querySelector<HTMLButtonElement>('[data-close-working]')?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        gesture.current = null;
        setActive(null);
        setPolygon([]);
        setFullScreen(false);
      }
      if (event.key !== 'Tab') return;
      // An embedded example is a bounded workspace, not a whole-site modal:
      // keep its frame identity and safe exit reachable by keyboard.
      if (containingViewport) return;
      const nodes = Array.from(
        workspace.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), select:not(:disabled), textarea, [tabindex="0"]',
        ) ?? [],
      ).filter((node) => !node.closest('[hidden]'));
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      background.forEach(({ node, inert, hidden }) => {
        if (!inert) node.removeAttribute('inert');
        if (hidden === null) node.removeAttribute('aria-hidden');
        else node.setAttribute('aria-hidden', hidden);
      });
      document.removeEventListener('keydown', handleKey);
      if (opener?.isConnected) opener.focus();
    };
  }, [fullScreen, containingViewport]);
  useEffect(() => {
    if (!expanded || !paperViewport.current) return;
    const element = paperViewport.current;
    const measure = () =>
      setFitHeight(
        element.clientWidth
          ? Math.ceil((element.clientHeight * CANVAS_WIDTH) / element.clientWidth)
          : 0,
      );
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [expanded, showText]);
  const [tool, setTool] = useState<Tool>('pen');
  const [colour, setColour] = useState<InkStroke['colour']>('ink');
  const [active, setActive] = useState<InkStroke | null>(null);
  const [polygon, setPolygon] = useState<InkPoint[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const undo = useRef<WorkingContent[]>([]);
  const redo = useRef<WorkingContent[]>([]);
  const valueRef = useRef(value);
  valueRef.current = value;
  const gesture = useRef<{
    id: number;
    tool: Tool;
    before: WorkingContent;
    points: InkPoint[];
    stroke: InkStroke | null;
    clientY: number;
  } | null>(null);
  const penSeen = useRef(false);
  const patternId = useId().replace(/:/g, '');
  const svg = useRef<SVGSVGElement>(null);

  const commit = (next: WorkingContent, before = valueRef.current) => {
    if (blocked) return;
    if (
      new TextEncoder().encode(JSON.stringify(next)).byteLength > MAX_CANVAS_BYTES ||
      next.strokes.length > 1000
    ) {
      setError('This page is full. Export it before starting another response.');
      return;
    }
    undo.current = [...undo.current.slice(-39), before];
    redo.current = [];
    onChange(next);
    setError(null);
  };
  const point = (event: ReactPointerEvent<SVGSVGElement>): InkPoint => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(
        CANVAS_WIDTH,
        Math.max(0, ((event.clientX - bounds.left) * CANVAS_WIDTH) / bounds.width),
      ),
      y: Math.min(
        displayedHeightRef.current,
        Math.max(0, ((event.clientY - bounds.top) * displayedHeightRef.current) / bounds.height),
      ),
      pressure: event.pointerType === 'pen' ? event.pressure || 0.5 : 0.5,
      time: event.timeStamp,
    };
  };
  const begin = (event: ReactPointerEvent<SVGSVGElement>) => {
    if ((blocked && tool !== 'pan') || event.button > 0) return;
    if (!expanded) {
      setFullScreen(true);
      return;
    }
    if (event.pointerType === 'pen') {
      penSeen.current = true;
      // Discard a touch that started just before the pen reached the screen.
      if (gesture.current?.stroke?.pointer === 'touch') {
        gesture.current = null;
        setActive(null);
      }
    }
    if ((event.pointerType === 'touch' && penSeen.current && tool !== 'pan') || gesture.current)
      return;
    const p = point(event);
    event.currentTarget.setPointerCapture(event.pointerId);
    const stroke: InkStroke = {
      id: crypto.randomUUID(),
      colour,
      width: penWidth,
      pointer:
        event.pointerType === 'pen' ? 'pen' : event.pointerType === 'touch' ? 'touch' : 'mouse',
      points: [p],
    };
    gesture.current = {
      id: event.pointerId,
      clientY: event.clientY,
      tool,
      before: valueRef.current,
      points: [p],
      stroke: tool === 'pen' ? stroke : null,
    };
    if (tool === 'pen') setActive(stroke);
    if (tool === 'lasso') setPolygon([p]);
  };
  const move = (event: ReactPointerEvent<SVGSVGElement>) => {
    const g = gesture.current;
    if (!g || g.id !== event.pointerId || (blocked && g.tool !== 'pan')) return;
    if (g.tool === 'pan') {
      if (paperViewport.current) paperViewport.current.scrollTop += g.clientY - event.clientY;
      g.clientY = event.clientY;
      return;
    }
    const p = point(event);
    if (g.points.length >= 2000) return;
    g.points.push(p);
    if (g.stroke) {
      g.stroke = { ...g.stroke, points: [...g.points] };
      setActive(g.stroke);
    }
    if (g.tool === 'lasso') setPolygon([...g.points]);
  };
  const end = (event: ReactPointerEvent<SVGSVGElement>) => {
    const g = gesture.current;
    if (!g || g.id !== event.pointerId) return;
    move(event);
    gesture.current = null;
    setActive(null);
    setPolygon([]);
    if (blocked) return;
    if (g.stroke) {
      const bottom = Math.max(...g.points.map((p) => p.y));
      commit(
        {
          ...g.before,
          height:
            bottom > g.before.height - 100
              ? Math.min(
                  MAX_CANVAS_HEIGHT,
                  Math.max(g.before.height + 600, Math.ceil(bottom + 100)),
                )
              : g.before.height,
          strokes: [...g.before.strokes, g.stroke],
        },
        g.before,
      );
    } else if (g.tool === 'eraser') {
      const next = g.before.strokes.filter(
        (stroke) => !g.points.some((p) => strokeNear(stroke, p)),
      );
      if (next.length !== g.before.strokes.length) commit({ ...g.before, strokes: next }, g.before);
    } else if (g.tool === 'lasso') {
      setSelected(
        g.before.strokes
          .filter((stroke) => stroke.points.some((p) => containsPoint(g.points, p)))
          .map((stroke) => stroke.id),
      );
      setTool('move');
    } else if (g.tool === 'move') {
      const from = g.points[0];
      const to = g.points[g.points.length - 1];
      commit(moveStrokes(g.before, selected, to.x - from.x, to.y - from.y), g.before);
    }
  };
  const exportSvg = () => {
    const source = svg.current;
    if (!source) return null;
    const copy = source.cloneNode(true) as SVGSVGElement;
    copy.setAttribute('width', String(CANVAS_WIDTH));
    copy.setAttribute('height', String(value.height));
    copy.setAttribute('viewBox', `0 0 ${CANVAS_WIDTH} ${value.height}`);
    copy.querySelectorAll('[data-export-colour]').forEach((node) => {
      const original = source.querySelector(
        `[data-export-colour="${node.getAttribute('data-export-colour')}"]`,
      );
      if (original) {
        const color = getComputedStyle(original).color;
        if (node.getAttribute('fill') !== 'none') node.setAttribute('fill', color);
        if (node.hasAttribute('stroke')) node.setAttribute('stroke', color);
      }
      if (node.hasAttribute('data-stroke-id')) node.setAttribute('opacity', '1');
    });
    copy.querySelectorAll('polyline').forEach((node) => node.remove());
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('width', '100%');
    rect.setAttribute('height', '100%');
    rect.setAttribute('fill', getComputedStyle(source).backgroundColor);
    copy.prepend(rect);
    copy.querySelectorAll('rect').forEach((rectangle) => {
      rectangle.setAttribute('width', String(CANVAS_WIDTH));
      rectangle.setAttribute('height', String(value.height));
    });
    return copy;
  };
  const printWorking = () => {
    const copy = exportSvg();
    if (!copy) return;
    const target = window.open('', '_blank');
    if (!target) {
      setError('Allow the print window to export your working as PDF.');
      return;
    }
    target.document.title = 'My math working';
    const style = target.document.createElement('style');
    style.textContent =
      '@page { size: A4; margin: 12mm; } body { margin: 0; font-family: sans-serif; } svg { width: 100%; height: auto; } .sheet { break-after: page; } pre { white-space: pre-wrap; overflow-wrap: anywhere; }';
    target.document.head.append(style);
    const title = target.document.createElement('h1');
    title.textContent = 'My math working';
    const text = target.document.createElement('pre');
    text.textContent = value.text;
    target.document.body.append(title);
    const pageHeight = 1250;
    for (let offset = 0; offset < value.height; offset += pageHeight) {
      const page = copy.cloneNode(true) as SVGSVGElement;
      const height = Math.min(pageHeight, value.height - offset);
      page.setAttribute('viewBox', `0 ${offset} ${CANVAS_WIDTH} ${height}`);
      page.setAttribute('height', String(height));
      const pattern = page.querySelector('pattern');
      if (pattern) {
        const id = `${pattern.id}-page-${offset}`;
        pattern.id = id;
        page
          .querySelectorAll('rect[fill^="url"]')
          .forEach((rect) => rect.setAttribute('fill', `url(#${id})`));
      }
      const section = target.document.createElement('section');
      if (offset + pageHeight < value.height) section.className = 'sheet';
      section.append(target.document.importNode(page, true));
      target.document.body.append(section);
    }
    target.document.body.append(text);
    target.focus();
    target.print();
  };
  const exportImage = async () => {
    const source = svg.current;
    if (!source) return;
    try {
      const copy = exportSvg();
      if (!copy) return;
      const url = URL.createObjectURL(
        new Blob([new XMLSerializer().serializeToString(copy)], { type: 'image/svg+xml' }),
      );
      try {
        const img = new Image();
        img.src = url;
        await img.decode();
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_WIDTH;
        const lines = value.text.split('\n').flatMap((line) => line.match(/.{1,85}/gu) ?? ['']);
        canvas.height = value.height + (value.text ? lines.length * 24 + 48 : 0);
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Export unavailable');
        context.fillStyle = getComputedStyle(source).backgroundColor;
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(img, 0, 0);
        context.fillStyle = getComputedStyle(source).color;
        context.font = '18px sans-serif';
        if (value.text)
          lines.forEach((line, index) =>
            context.fillText(line, 16, value.height + 32 + index * 24),
          );
        const blob = await new Promise<Blob>((resolve, reject) =>
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Export failed'))), 'image/png'),
        );
        const download = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = download;
        link.download = 'my-math-working.png';
        link.click();
        setTimeout(() => URL.revokeObjectURL(download), 1000);
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch {
      setError('The image could not be exported. Your working is still here.');
    }
  };
  const iconButton =
    'inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-xl text-ink-soft transition-colors hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-sky disabled:opacity-30';
  const changeTool = (next: Tool) => {
    setTool(next);
    setPanel(null);
    setSelected([]);
  };
  const toggleScreen = () => {
    gesture.current = null;
    setActive(null);
    setPolygon([]);
    setShowMore(false);
    setFullScreen(!fullScreen);
  };
  const undoWorking = () => {
    const previous = undo.current.pop();
    if (previous) {
      redo.current.push(value);
      onChange(previous);
      setSelected([]);
    }
  };
  const redoWorking = () => {
    const next = redo.current.pop();
    if (next) {
      undo.current.push(value);
      onChange(next);
    }
  };
  const saveStatus = status ?? (locked ? 'Submitted · read only' : 'Ready to write');
  const statusColour = /saved/i.test(saveStatus)
    ? 'bg-brand-mint'
    : /draft|device/i.test(saveStatus)
      ? 'bg-brand-sky'
      : /fail|conflict|unavailable|offline/i.test(saveStatus)
        ? 'bg-brand-coral'
        : 'bg-stone2';
  const content = (
    <WorkingOverlayContext.Provider
      value={{
        panel,
        setPanel,
        fullScreen: expanded,
        openFullScreen: () => {
          if (!embedded) setFullScreen(true);
        },
      }}
    >
      <section
        ref={workspace}
        style={
          {
            '--working-viewport-height': `${viewport.height}px`,
            '--working-viewport-top': `${viewport.top}px`,
            '--working-demo-identity-height': fullScreen && isLocalStudyExperience() ? '48px' : '0px',
            ...(fullScreen
              ? {
                  height: viewport.height,
                  top: viewport.top,
                  paddingLeft: 'env(safe-area-inset-left)',
                  paddingRight: 'env(safe-area-inset-right)',
                }
              : {}),
          } as CSSProperties
        }
        role={fullScreen ? (containingViewport ? 'region' : 'dialog') : undefined}
        aria-modal={fullScreen && !containingViewport ? true : undefined}
        aria-label={fullScreen ? 'Full-screen math working' : 'Math working canvas'}
        className={clsx(
          fullScreen
            ? containingViewport
              ? 'fixed inset-0 z-[100] flex h-full w-full flex-col'
              : 'fixed inset-0 z-[100] flex h-[100dvh] w-screen flex-col'
            : embedded
              ? 'relative flex min-h-0 flex-1 flex-col overflow-hidden'
              : 'relative mt-5 overflow-hidden rounded-2xl border border-hairline',
          'bg-canvas-pure',
        )}
      >
        {fullScreen && !containingViewport && <StudyDemoIdentityStrip />}
        {!embedded && (
          <WorkingCanvasHeader
            fullScreen={fullScreen}
            contextLabel={contextLabel}
            status={saveStatus}
            statusColour={statusColour}
            buttonClass={iconButton}
            toggleScreen={toggleScreen}
            management={Boolean(management)}
            managementOpen={showManagement}
            toggleManagement={() => setShowManagement(!showManagement)}
            tutor={tutor}
          />
        )}
        {embedded && (
          <p
            role="status"
            className="shrink-0 border-b border-hairline px-4 py-2 text-xs text-slate2"
          >
            {saveStatus}
          </p>
        )}
        {fullScreen && showManagement && management && (
          <WorkingPageManagement
            status={saveStatus}
            onClose={() => setShowManagement(false)}
            buttonClass={iconButton}
          >
            {management}
          </WorkingPageManagement>
        )}
        {onQuestionChange && (
          <label
            className={clsx(
              'mx-3 my-2 block shrink-0 text-xs font-bold text-slate2',
              fullScreen && panel === WORKING_PANEL.TUTOR && 'md:mr-[420px]',
            )}
          >
            Your question (optional)
            <textarea
              aria-label="Your question"
              value={question ?? ''}
              disabled={blocked}
              maxLength={5000}
              rows={2}
              placeholder="Type the question, or add a photo in Tutor…"
              className="input-k12 mt-1 w-full resize-none text-sm"
              onChange={(event) => onQuestionChange(event.target.value)}
            />
          </label>
        )}
        {expanded && question && !onQuestionChange && (
          <aside
            className="relative z-20 mx-3 my-2 shrink-0 rounded-2xl border border-hairline bg-canvas-pure p-3 shadow-sm md:w-[380px] md:self-start"
            aria-label="Current question"
          >
            <button
              type="button"
              aria-label="Show current question"
              aria-expanded={showQuestion}
              className="flex w-full items-center gap-2 text-left text-xs font-bold text-ink"
              onClick={() => setShowQuestion(!showQuestion)}
            >
              <BookOpen size={16} className="shrink-0 text-brand-sky" />
              <span className="min-w-0 flex-1 truncate">
                <AcademyRichText text={question} />
              </span>
              <ChevronDown size={16} className={clsx('shrink-0', showQuestion && 'rotate-180')} />
            </button>
            {showQuestion && (
              <div className="mt-3 max-h-[25dvh] overflow-auto border-t border-hairline pt-3 text-sm leading-relaxed text-ink">
                <AcademyRichText text={question} />
              </div>
            )}
          </aside>
        )}
        <div
          ref={paperViewport}
          data-working-paper-scroll
          className={clsx(
            'relative overflow-auto',
            expanded ? 'min-h-0 flex-1 overscroll-contain pb-24' : 'max-h-44',
          )}
        >
          <svg
            ref={svg}
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Handwritten working. Use Text steps below for keyboard input."
            tabIndex={locked ? -1 : 0}
            viewBox={`0 0 ${CANVAS_WIDTH} ${displayedHeight}`}
            style={{ aspectRatio: `${CANVAS_WIDTH}/${displayedHeight}` }}
            className={clsx(
              'block w-full bg-canvas-pure',
              (!blocked || tool === 'pan') && 'touch-none select-none',
              tool === 'pan'
                ? 'cursor-grab active:cursor-grabbing'
                : !blocked && 'cursor-crosshair',
            )}
            onPointerDown={begin}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={() => {
              gesture.current = null;
              setActive(null);
              setPolygon([]);
            }}
            onKeyDown={(event) => {
              if (blocked || !selected.length) return;
              const delta: Record<string, [number, number]> = {
                ArrowLeft: [-10, 0],
                ArrowRight: [10, 0],
                ArrowUp: [0, -10],
                ArrowDown: [0, 10],
              };
              if (delta[event.key]) {
                event.preventDefault();
                commit(moveStrokes(value, selected, ...delta[event.key]));
              }
              if (event.key === 'Delete' || event.key === 'Backspace') {
                event.preventDefault();
                commit({
                  ...value,
                  strokes: value.strokes.filter((s) => !selected.includes(s.id)),
                });
                setSelected([]);
              }
            }}
          >
            <defs>
              <pattern id={patternId} width="25" height="25" patternUnits="userSpaceOnUse">
                {value.background === 'grid' ? (
                  <path
                    d="M 25 0.5 L 0.5 0.5 0.5 25"
                    fill="none"
                    data-export-colour="paper"
                    className="text-stone2"
                    opacity="0.4"
                    stroke="currentColor"
                    strokeWidth="1"
                  />
                ) : (
                  <circle
                    cx="2"
                    cy="2"
                    r="1"
                    data-export-colour="paper"
                    className="text-stone2"
                    opacity="0.4"
                    fill="currentColor"
                  />
                )}
              </pattern>
            </defs>
            {value.background !== 'blank' && (
              <rect width="100%" height="100%" fill={`url(#${patternId})`} />
            )}
            {[...value.strokes, ...(active ? [active] : [])].map((stroke) => (
              <path
                key={stroke.id}
                data-export-colour={stroke.colour}
                data-stroke-id={stroke.id}
                d={inkPath(stroke)}
                fill="currentColor"
                className={colours[stroke.colour]}
                opacity={selected.includes(stroke.id) ? 0.5 : 1}
              />
            ))}
            {polygon.length > 1 && (
              <polyline
                points={polygon.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke="currentColor"
                className="text-brand-sky"
                strokeWidth="2"
                strokeDasharray="8 4"
              />
            )}
          </svg>
          {!value.strokes.length && !value.text && (
            <div className="pointer-events-none absolute inset-x-0 top-1/3 text-center">
              <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-wash-sky text-brand-sky">
                <Pencil size={22} />
              </span>
              <p className="text-sm font-bold text-ink">A place to work it out</p>
              <p className="mt-1 text-xs text-slate2">
                {expanded
                  ? 'Write a step, sketch an idea, or use Text steps.'
                  : 'Tap the page to start writing in full screen.'}
              </p>
            </div>
          )}
        </div>
        <WorkingToolbar
          fullScreen={expanded}
          blocked={blocked}
          locked={locked}
          tool={tool}
          buttonClass={iconButton}
          canUndo={undo.current.length > 0}
          showText={showText}
          showMore={showMore}
          changeTool={changeTool}
          undo={undoWorking}
          openWorking={toggleScreen}
          toggleText={() => setShowText(!showText)}
          toggleMore={() => setShowMore(!showMore)}
        />
        {fullScreen && selected.length === 0 && (
          <p className="pointer-events-none absolute bottom-24 left-0 right-0 z-10 text-center text-[11px] font-medium text-slate2">
            {tool === 'pan'
              ? 'Move page · drag to scroll'
              : locked
                ? 'Submitted working · read only'
                : tool === 'eraser'
                  ? 'Eraser · touch a stroke to remove it'
                  : tool === 'lasso'
                    ? 'Selection · circle strokes to move them'
                    : 'Pen · write your next step'}
          </p>
        )}
        {selected.length > 0 && fullScreen && (
          <div className="absolute bottom-24 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3 rounded-xl border border-hairline bg-canvas-pure px-3 text-xs font-bold text-ink shadow-sm">
            <span>{selected.length} selected · drag to move</span>
            <button
              type="button"
              className={iconButton}
              aria-label="Delete selected strokes"
              disabled={blocked}
              onClick={() => {
                commit({
                  ...value,
                  strokes: value.strokes.filter((stroke) => !selected.includes(stroke.id)),
                });
                setSelected([]);
              }}
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
        {showMore && (
          <WorkingSheet
            title="Pen & page"
            label="Working preferences"
            flow
            onClose={() => setShowMore(false)}
          >
            <button
              type="button"
              className="btn-pill-secondary mb-4 w-full md:hidden"
              disabled={blocked}
              onClick={() => changeTool('lasso')}
            >
              Select and move strokes
            </button>
            {!locked && (
              <>
                <p className="mb-2 text-xs font-bold text-slate2">Ink</p>
                <div className="flex gap-2">
                  {(Object.keys(colours) as Array<InkStroke['colour']>).map((key) => (
                    <button
                      key={key}
                      type="button"
                      aria-label={`${key} pen`}
                      aria-pressed={colour === key}
                      disabled={blocked}
                      className={clsx(
                        iconButton,
                        colours[key],
                        colour === key && 'bg-surface ring-2 ring-hairline',
                      )}
                      onClick={() => {
                        setColour(key);
                        changeTool('pen');
                      }}
                    >
                      <span
                        className={clsx(
                          'h-5 w-5 rounded-full',
                          key === 'ink'
                            ? 'bg-ink'
                            : key === 'sky'
                              ? 'bg-brand-sky'
                              : 'bg-brand-coral',
                        )}
                      />
                    </button>
                  ))}
                </div>
                <p className="mb-2 mt-4 text-xs font-bold text-slate2">Pen size</p>
                <div className="flex gap-2">
                  {[2, 4, 8].map((size) => (
                    <button
                      key={size}
                      type="button"
                      aria-label={`Pen size ${size}`}
                      aria-pressed={penWidth === size}
                      disabled={blocked}
                      className={clsx(iconButton, penWidth === size && 'bg-wash-sky')}
                      onClick={() => setPenWidth(size)}
                    >
                      <span
                        className="rounded-full bg-ink"
                        style={{ width: size + 2, height: size + 2 }}
                      />
                    </button>
                  ))}
                </div>
                <label className="mt-4 flex items-center justify-between text-xs font-bold text-slate2">
                  Paper
                  <select
                    aria-label="Working paper background"
                    disabled={blocked}
                    className="min-h-11 rounded-xl border border-hairline bg-canvas-pure px-3 text-sm text-ink"
                    value={value.background}
                    onChange={(event) => {
                      const background = event.target.value;
                      if (background === 'blank' || background === 'grid' || background === 'dots')
                        commit({ ...value, background });
                    }}
                  >
                    <option value="blank">Blank</option>
                    <option value="grid">Grid</option>
                    <option value="dots">Dots</option>
                  </select>
                </label>
              </>
            )}
            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-hairline pt-3">
              <button
                type="button"
                aria-label="Redo working"
                disabled={blocked || !redo.current.length}
                className="btn-pill-secondary flex items-center justify-center gap-2 text-xs"
                onClick={redoWorking}
              >
                <Redo2 size={16} />
                Redo
              </button>
              <button
                type="button"
                disabled={blocked || value.height >= MAX_CANVAS_HEIGHT}
                className="btn-pill-secondary flex items-center justify-center gap-2 text-xs"
                onClick={() =>
                  commit({ ...value, height: Math.min(MAX_CANVAS_HEIGHT, value.height + 600) })
                }
              >
                <Plus size={16} />
                Add space below
              </button>
              <button
                type="button"
                className="btn-pill-secondary flex items-center justify-center gap-2 text-xs"
                onClick={() => void exportImage()}
              >
                <Download size={16} />
                PNG
              </button>
              <button
                type="button"
                className="btn-pill-secondary flex items-center justify-center gap-2 text-xs"
                onClick={printWorking}
              >
                <Printer size={16} />
                Print / PDF
              </button>
            </div>
          </WorkingSheet>
        )}
        {!expanded && value.text && (
          <div
            aria-label="Saved text steps"
            className="mx-4 mb-4 whitespace-pre-wrap text-sm text-ink-soft"
          >
            <AcademyRichText text={value.text} />
          </div>
        )}
        {showText && (
          <WorkingTextSteps
            id={`${patternId}-steps`}
            value={value.text}
            blocked={blocked}
            onClose={() => setShowText(false)}
            onChange={(text) => commit({ ...value, text })}
          />
        )}
        {error && (
          <p
            role="alert"
            className="absolute bottom-24 left-3 right-3 z-30 rounded-xl bg-wash-peach p-3 text-sm text-danger"
          >
            {error}
          </p>
        )}
      </section>
    </WorkingOverlayContext.Provider>
  );
  return fullScreen ? createPortal(content, containingViewport ?? document.body) : content;
}
